import {checkIsDemoAccount} from "@/helpers/checkIsDemoAccount";
import {generateInitialState} from "@/helpers/generateInitialState";
import {removeMedianToken} from "@/helpers/removeMedianToken";
import {authInitialState} from "@/features/initialStates";
import {createSlice} from "@reduxjs/toolkit";
import {signIn, activateKit} from "./authActions";

const initialState = await generateInitialState("auth", authInitialState);
const websiteId = window.config.websiteId;

let websiteURL;
if (websiteId == 1) {
  websiteURL = "forthwithlife";
} else if (websiteId == 2) {
  websiteURL = "sportsbloodtests";
} else {
  websiteURL = "biomarkertracking";
}

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state, action) => {
      state.isAuthenticated = false;
      state.isAuthenticating = false;
      state.isLoggingOut = true;
      state.token = "";

      const appWindow = window;
      appWindow.postMessage(JSON.stringify({type: "logout", response: ""}), "*");

      const theme = localStorage.getItem("theme");
      const loadedNewStorage = localStorage.getItem("loadedNewStorage");

      document.cookie =
        "forthapptoken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=" + websiteURL + ".co.uk";
      localStorage.clear();

      theme && localStorage.setItem("theme", theme);
      loadedNewStorage && localStorage.setItem("loadedNewStorage", loadedNewStorage);

      if (websiteId === 1) {
        removeMedianToken();
      }
    },
    clearAuthErrorMessage: (state, action) => {
      state.hasFailed = false;
      state.errorMsg = "";
    },
    grantPermissionToPartnerAuth: (state, action) => {
      state.isFetching = false;
      state.hasFailed = false;
      state.grantPermissionToPartner = action.payload;
    },
    refreshToken: (state, action) => {
      state.isFetching = false;
      state.hasFailed = false;
      state.token = action.payload.token;
    },
    clearActivationCode: (state, action) => {
      state.isFetching = false;
      state.hasFailed = false;
      state.errorMsg = "";
      state.activationCode = "";
    },
    setActivationStatus: (state, action) => {
      state.isFetching = false;
      state.hasFailed = false;
      state.errorMsg = "";
      state.activationStatus = action.payload;
      // "FAILED" or "SUCCESS"
      // Change this back to action.payload.activationStatus after testing
    },
    clearActivationStatus: (state, action) => {
      state.isFetching = false;
      state.hasFailed = false;
      state.errorMsg = "";
      delete state.activationStatus;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(signIn.pending, (state, action) => {
        state.isAuthenticated = false;
        state.isAuthenticating = true;
        state.isFetching = true;
        state.hasFailed = false;
        state.errorMsg = "";
      })
      .addCase(signIn.fulfilled, (state, action) => {
        const {token, ID} = action.payload;

        localStorage.setItem("token", token);
        const isDemoAccount = checkIsDemoAccount(ID);

        if (!isDemoAccount) {
          document.cookie = "forthapptoken=" + token + "; domain=" + websiteURL + ".co.uk";
        }

        state.token = token;
        state.data = action.payload;
        state.isAuthenticated = true;
        state.isAuthenticating = false;
        state.isFetching = false;
        state.hasFailed = false;
        state.errorMsg = "";
      })
      .addCase(signIn.rejected, (state, action) => {
        const {message = "Something went wrong"} = action.payload || {};

        state.isAuthenticated = false;
        state.isAuthenticating = false;
        state.isFetching = false;
        state.hasFailed = true;
        state.errorMsg = message;
      })
      .addCase(activateKit.pending, state => {
        state.isFetching = true;
        state.hasFailed = false;
        state.activationCode = "";
        state.errorMsg = "";
      })
      .addCase(activateKit.fulfilled, (state, action) => {
        state.isFetching = false;
        state.hasFailed = false;
        state.activationCode = action.payload[0].activationCode;
      })
      .addCase(activateKit.rejected, (state, action) => {
        const {message = "Something went wrong"} = action.payload || {};

        state.isFetching = false;
        state.hasFailed = true;
        state.errorMsg = message;
      });
  },
  selectors: {
    authStateSelector: state => state,
    authStatusSelector: state => ({
      isFetching: state?.isFetching || false,
      hasFailed: state?.hasFailed || false,
      errorMsg: state?.errorMsg || "",
    }),
    activationStatusSelector: state => state?.activationStatus || undefined,
  },
});
export const {
  logout,
  clearAuthErrorMessage,
  grantPermissionToPartnerAuth,
  refreshToken,
  clearActivationCode,
  setActivationStatus,
  clearActivationStatus,
} = authSlice.actions;
export const {authStateSelector, authStatusSelector, activationStatusSelector} = authSlice.selectors;
export default authSlice.reducer;
